import React, { useState, useEffect } from 'react';
import { HelpCircle, MessageSquare, Send, Loader2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useWorkspace } from '@/context/WorkspaceContext';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const SupportPage = () => {
  const { currentWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    priority: 'normal',
    message: ''
  });

  useEffect(() => {
    fetchTickets();
  }, [currentWorkspace]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/support/tickets`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      return toast.error('Please fill in all required fields');
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, { ...newTicket, workspace_id: currentWorkspace?._id });
      toast.success('Support ticket created successfully');
      setNewTicket({ subject: '', category: 'general', priority: 'normal', message: '' });
      setShowNewTicket(false);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      resolved: 'bg-green-100 text-green-600',
      closed: 'bg-zinc-100 text-zinc-600'
    };
    return badges[status] || badges.open;
  };

  const faqs = [
    { question: 'How do credits work?', answer: 'Credits are used to generate designs and export pages. Each design generation costs 1 credit, and exports cost 2 credits per page.' },
    { question: 'Can I invite team members?', answer: 'Yes! Workspace owners can invite team members from the Team page. Invited members share the workspace credits pool.' },
    { question: 'How do I export my design?', answer: 'Once your design is ready, click the "Export" button on the design preview page. You can export as HTML/CSS or request a WordPress build.' },
    { question: 'What themes are available?', answer: 'We offer a curated selection of WordPress themes. If you need a specific theme, our system can search WordPress.org and request its installation.' }
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title="Support" />
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Card className="bg-white border-zinc-200 mb-6">
            <CardHeader>
              <CardTitle className="text-zinc-900 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-amber-500" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-zinc-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 transition-colors"
                  >
                    <span className="text-zinc-900 font-medium">{faq.question}</span>
                    {expandedFaq === index ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                  </button>
                  {expandedFaq === index && <div className="px-4 pb-4 text-zinc-600 text-sm">{faq.answer}</div>}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-white border-zinc-200 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-zinc-900 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-amber-500" />
                    Support Tickets
                  </CardTitle>
                  <CardDescription className="text-zinc-500">Create or view your support tickets</CardDescription>
                </div>
                <Button onClick={() => setShowNewTicket(!showNewTicket)} className="bg-amber-500 hover:bg-amber-600" data-testid="new-ticket-btn">
                  <Plus className="w-4 h-4 mr-2" />New Ticket
                </Button>
              </div>
            </CardHeader>
            {showNewTicket && (
              <CardContent className="border-t border-zinc-100 pt-6">
                <form onSubmit={handleSubmitTicket} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Subject *</label>
                    <Input value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })} placeholder="Brief description" className="bg-zinc-50 border-zinc-200" data-testid="ticket-subject-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">Category</label>
                      <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })} className="w-full h-10 px-3 rounded-md bg-zinc-50 border border-zinc-200">
                        <option value="general">General Inquiry</option>
                        <option value="billing">Billing</option>
                        <option value="technical">Technical Issue</option>
                        <option value="feature">Feature Request</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">Priority</label>
                      <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })} className="w-full h-10 px-3 rounded-md bg-zinc-50 border border-zinc-200">
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Message *</label>
                    <Textarea value={newTicket.message} onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })} placeholder="Describe your issue..." className="bg-zinc-50 border-zinc-200 min-h-[100px]" data-testid="ticket-message-input" />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="submit-ticket-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Submit
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)} className="border-zinc-200">Cancel</Button>
                  </div>
                </form>
              </CardContent>
            )}
          </Card>

          <Card className="bg-white border-zinc-200">
            <CardHeader><CardTitle className="text-zinc-900">Your Tickets</CardTitle></CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
              ) : tickets.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-zinc-300 mb-3" />
                  <p className="text-zinc-600">No support tickets yet</p>
                  <p className="text-zinc-400 text-sm">Create a ticket if you need help</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {tickets.map((ticket, index) => (
                    <div key={ticket._id || index} className="p-4 hover:bg-zinc-50">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-zinc-900 font-medium">{ticket.subject}</h4>
                          <p className="text-zinc-400 text-sm">#{ticket.ticket_id || index + 1} - {new Date(ticket.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(ticket.status)}`}>{ticket.status}</span>
                      </div>
                      <p className="text-zinc-500 text-sm line-clamp-2">{ticket.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
